import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { isAxiosError } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/core/Button';
import { ButtonPanel } from '@/components/core/ButtonPanel';
import { Checkbox } from '@/components/core/Checkbox';
import { FocusAttributeProvider } from '@/dialogs/item-editor/Context/FocusAttributeProvider';
import { useDataModel } from '@/hooks/useDataModel';
import { useToast } from '@/hooks/useToast';
import { useTranslation } from '@/hooks/useTranslation';
import { queryClient } from '@/services/query-client';

import {
    FilterAttributeOption,
    FilterGroup,
    FilterLogicalOperator,
    ItemsSearchFilter,
    ItemsSearchFilterQuery,
} from './FilterBuilder.types';
import {
    createEmptyFilterGroups,
    createEmptyGroup,
    parseFilterQuery,
    serializeFilterGroups,
} from './filterGroups';
import { useDeleteFilterProfile, useSaveFilterProfile } from './filterProfiles.queries';
import { GroupBlock, LogicalOperatorSelect } from './GroupBlock';
import { SaveFilterForm } from './SaveFilterForm';

interface FilterBuilderProps {
    open: boolean;
    initialFilter?: ItemsSearchFilter | null;
    attributes?: FilterAttributeOption[];
    onClose: () => void;
    onSaved?: (filter: ItemsSearchFilter) => void;
    /** Live preview: same as Angular `updateQuery` while the builder is open. */
    onQueryChange?: (query: ItemsSearchFilterQuery) => void;
}

function conflictFilterName(error: unknown, fallback: string): string {
    if (!isAxiosError(error)) {
        return fallback;
    }
    const data = error.response?.data as { data?: string | string[] } | undefined;
    const value = data?.data;
    if (Array.isArray(value) && value[0]) {
        return String(value[0]);
    }
    if (typeof value === 'string' && value) {
        return value;
    }
    return fallback;
}

const FilterBuilder = ({
    open,
    initialFilter,
    attributes: attributesProp,
    onClose,
    onSaved,
    onQueryChange,
}: FilterBuilderProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const { dataModel } = useDataModel();
    const [groups, setGroups] = useState<FilterGroup[]>(() => (
        initialFilter?.itemsQuery?.keyword
            ? parseFilterQuery(initialFilter.itemsQuery.keyword)
            : createEmptyFilterGroups()
    ));
    const [isSaveOpen, setIsSaveOpen] = useState(false);
    const onQueryChangeRef = useRef(onQueryChange);
    onQueryChangeRef.current = onQueryChange;

    const attributes = useMemo<FilterAttributeOption[]>(() => {
        if (attributesProp?.length) {
            return attributesProp;
        }
        if (!dataModel) {
            return [];
        }
        const fromLayout = dataModel.filteredLayoutAttributes?.('filter') ?? [];
        if (fromLayout.length) {
            return fromLayout;
        }
        return dataModel.allAttributes?.() ?? [];
    }, [attributesProp, dataModel]);

    useEffect(() => {
        if (!open) {
            return;
        }
        setIsSaveOpen(false);
        if (initialFilter?.itemsQuery?.keyword) {
            setGroups(parseFilterQuery(initialFilter.itemsQuery.keyword));
            return;
        }
        setGroups(createEmptyFilterGroups());
    }, [open, initialFilter]);

    useEffect(() => {
        if (!open) {
            return;
        }
        const nextQuery = serializeFilterGroups(groups);
        if (!nextQuery.keyword && initialFilter?.itemsQuery?.keyword) {
            return;
        }
        onQueryChangeRef.current?.(nextQuery);
    }, [open, groups, initialFilter]);

    const saveMutation = useSaveFilterProfile();
    const deleteMutation = useDeleteFilterProfile();
    const isSaving = saveMutation.isPending || deleteMutation.isPending;

    const updateGroup = (index: number, nextGroup: FilterGroup) => {
        setGroups((current) => {
            if (nextGroup.rules.length === 0) {
                const remaining = current.filter((_, groupIndex) => groupIndex !== index);
                if (!remaining.length) {
                    return remaining;
                }
                return remaining.map((group, groupIndex) => (
                    groupIndex === 0
                        ? { ...group, operator: undefined, notOperator: false }
                        : group
                ));
            }
            return current.map((group, groupIndex) => (groupIndex === index ? nextGroup : group));
        });
    };

    const addGroup = () => {
        setGroups((current) => [...current, createEmptyGroup('OR')]);
    };

    const setGroupOperator = (index: number, operator: FilterLogicalOperator) => {
        updateGroup(index, { ...groups[index], operator });
    };

    const setGroupNotOperator = (index: number, notOperator: boolean) => {
        updateGroup(index, { ...groups[index], notOperator });
    };

    const handleSave = async (name: string, isPublic: boolean) => {
        if (!name) {
            toast.error(t('ITEMS_SEARCH_FILTER.MISSING_FILTER_NAME'));
            return;
        }

        const itemsQuery = serializeFilterGroups(groups);
        const payload: ItemsSearchFilter = {
            name,
            isPublic,
            itemsQuery,
        };

        try {
            if (initialFilter?.name && initialFilter.name === name) {
                await deleteMutation.mutateAsync({ identifier: initialFilter.name });
            }
            const saved = await saveMutation.mutateAsync(payload);
            await queryClient.invalidateQueries({ queryKey: ['browse', 'filters'] });
            await queryClient.invalidateQueries({ queryKey: ['filterProfiles'] });
            toast.success(t('ITEMS_SEARCH_FILTER.SAVE_SUCCESS_MESSAGE'));
            setIsSaveOpen(false);
            onSaved?.(saved ?? payload);
            onClose();
        } catch (error) {
            if (isAxiosError(error) && error.response?.status === 409) {
                toast.error(t('ITEMS_SEARCH_FILTER.SAVE_ERROR_CONFLICT_MESSAGE', {
                    filterName: conflictFilterName(error, name),
                }));
                return;
            }
            toast.error(t('ITEMS_SEARCH_FILTER.SAVE_ERROR_MESSAGE'));
        }
    };

    return (
        <>
            {open && (
                <FocusAttributeProvider>
                    <section
                        className="tw-flex tw-flex-col tw-gap-8 tw-items-end tw-w-full tw-min-w-0 tw-mt-8 tw-mb-8 tw-bg-fill-primary tw-border tw-border-solid tw-border-primary
                            tw-rounded-lg tw-pt-3 tw-px-5 tw-pb-8"
                        aria-labelledby="custom-filter-title"
                    >
                        <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-w-full tw-min-w-0 tw-border-b
                            tw-border-t-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-divider tw-pb-3">
                            <p
                                id="custom-filter-title"
                                className="tw-m-0 tw-min-w-0 tw-flex-1 lg:tw-flex-none tw-text-t-sm lg:tw-text-t-base tw-font-bold tw-text-t-primary"
                            >
                                {t('ITEMS_SEARCH_FILTER.CUSTOM_FILTER')}
                            </p>
                            <div className="tw-flex tw-items-center tw-shrink-0 tw-gap-3 lg:tw-gap-2">
                                <Button variant="outline" onClick={() => setIsSaveOpen(true)}>
                                    {t('ITEMS_SEARCH_FILTER.SAVE_FILTER')}
                                </Button>
                                <ButtonPanel
                                    isDisabled={false}
                                    icon={XMarkIcon}
                                    variant="primary"
                                    tooltip={t('ITEMS_SEARCH_FILTER.CLOSE_CUSTOM_FILTER')}
                                    aria-label={t('ITEMS_SEARCH_FILTER.CLOSE_CUSTOM_FILTER')}
                                    onClick={onClose}
                                />
                            </div>
                        </div>
                        <div className="tw-flex tw-flex-col tw-gap-5 tw-items-end tw-w-full tw-min-w-0 tw-max-w-full">
                            {groups.map((group, index) => (
                                <div key={index} className="tw-flex tw-flex-col tw-gap-5 tw-items-stretch tw-w-full tw-min-w-0">
                                    {index > 0 && (
                                        <div className="tw-flex tw-gap-5 tw-items-center tw-justify-center tw-w-full">
                                            <LogicalOperatorSelect
                                                value={group.operator ?? 'OR'}
                                                onChange={(operator) => setGroupOperator(index, operator)}
                                                ariaLabel={t('ITEMS_SEARCH_FILTER.OPERATOR_OR')}
                                            />
                                            <label className="tw-flex tw-gap-2 tw-items-center">
                                                <Checkbox
                                                    isChecked={group.notOperator}
                                                    onCheckedChange={(checked) => setGroupNotOperator(index, checked === true)}
                                                    aria-label={t('ITEMS_SEARCH_FILTER.NOT')}
                                                />
                                                <span className="tw-text-t-sm lg:tw-text-t-base tw-text-t-primary">{t('ITEMS_SEARCH_FILTER.NOT')}</span>
                                            </label>
                                        </div>
                                    )}
                                    <GroupBlock
                                        group={group}
                                        attributes={attributes}
                                        onChange={(nextGroup) => updateGroup(index, nextGroup)}
                                    />
                                </div>
                            ))}
                            <Button
                                variant="ghost"
                                hasIcon={true}
                                icon={PlusIcon}
                                onClick={addGroup}
                            >
                                {t('ITEMS_SEARCH_FILTER.ADD_NEW_GROUP')}
                            </Button>
                        </div>
                    </section>
                </FocusAttributeProvider>
            )}
            {/* Stay mounted after the builder closes so Radix can render open={false}
                and release the body pointer-events lock (radix-ui/primitives#1859). */}
            <SaveFilterForm
                open={isSaveOpen}
                defaultName={initialFilter?.name ?? ''}
                defaultIsPublic={initialFilter?.isPublic ?? false}
                isSaving={isSaving}
                onClose={() => setIsSaveOpen(false)}
                onSave={handleSave}
            />
        </>
    );
};

FilterBuilder.displayName = 'FilterBuilder';

export { FilterBuilder };
export type { FilterBuilderProps };
